import {
  curry,
  compose,
  concat,
  head,
  equals,
  not,
  drop,
  dropLast,
  __,
  test,
  path,
  complement,
  replace,
  min,
} from 'ramda'
import { isNull } from 'utils/types'
import { splitToFloats } from 'utils/timers'
import {
  TIMER_MASK_CHAR,
  timerSubstRange,
  timerMaxSeconds,
} from 'constants/ui/timers'

// helpers
const notEquals = complement(equals)
const cannotUpdate = compose(not, equals(TIMER_MASK_CHAR), head)
const emptyTimerValueRegex = /^[0.:]*$/

const removeSeparators = replace(/[\D]*/g, '')
const SEPARATORS = [':', '.']
const isSeparatorChar = (char) => SEPARATORS.includes(char)

const calculateSeconds = (hours = 0, minutes, seconds) =>
  seconds + minutes * 60 + hours * 60 * 60

/**
 * getMaskedValue
 *
 * @param {number} value
 * @param {string} mask
 * @returns {string}
 *
 * @example
 *    getMaskedValue(6553.4, '9:99:99.9')
 *    // => '1:49:13.4'
 */
export const getMaskedValue = (value, mask) => {
  const maxValue = timerMaxSeconds(mask)
  const newValue = min(value / 10, maxValue)

  // different masks require a different substring
  const substrRange = timerSubstRange(mask)

  return new Date(newValue * 1000).toISOString().substr(...substrRange)
}

/**
 * getSecondsValue
 *
 * @param {string} value
 * @param {string} mask
 * @returns {number}
 *
 * @example
 *    getSecondsValue('9:99:99.9', '9:99:99.9')
 *    // => 65534
 */
export const getRawValue = (value, mask) => {
  const [seconds, minutes, hours] = splitToFloats(value)

  const totalSeconds = calculateSeconds(hours, minutes, seconds)
  const maxValue = timerMaxSeconds(mask)

  const newValue = min(totalSeconds, maxValue)

  return newValue * 10
}

/**
 * addSeparators
 *
 * @param {string} mask
 * @param {string} value
 * @returns {string}
 *
 * @example
 *    addSeparators('9:99:99.9', '123456')
 *    // => 1:23:45.6
 *
 *    addSeparators('9:99', '123')
 *    // => 1:23
 */
export const addSeparators = curry((mask, value) => {
  let newValue = ''
  let valuePosition = 0
  let maskPosition = 0

  while (newValue.length < mask.length) {
    const maskChar = mask[maskPosition]

    if (isSeparatorChar(maskChar)) {
      newValue += maskChar
      maskPosition += 1
    } else {
      newValue += value[valuePosition]
      valuePosition += 1
      maskPosition += 1
    }
  }

  return newValue
})

/**
 * appendToTimerValue
 *
 * @param {char} userInput
 * @param {string} value
 * @param {string} mask
 * @returns {string}
 *
 * @example
 *    appendToTimerValue('3', '0:12', '9:99')
 *    // => '1:23'
 */
export const appendToTimerValue = (userInput, value, mask) => {
  if (cannotUpdate(value)) return value

  const updateValue = compose(
    addSeparators(mask),
    concat(__, userInput),
    drop(1),
    removeSeparators
  )

  return updateValue(value)
}

/**
 * popTimerValue
 *
 * @param {char} userInput
 * @param {string} value
 * @param {string} mask
 * @returns {string}
 *
 * @example
 *    popTimerValue('1:23', '9:99')
 *    // => '0:12'
 */
export const popTimerValue = (value, mask) => {
  if (test(emptyTimerValueRegex, value)) return value

  const updateValue = compose(
    addSeparators(mask),
    concat(TIMER_MASK_CHAR),
    dropLast(1),
    removeSeparators
  )

  return updateValue(value)
}

/**
 * beforeMaskedStateChange
 *
 * handler to hook into react-input-mask change event
 */
export const beforeMaskedValueChange = (mask) => (
  newState,
  oldState,
  userInput
) => {
  const { value: newValue } = newState
  const { value: oldValue } = oldState

  const getCursorPosition = path(['selection', 'start'])
  const getCursorSelectionLength = path(['selection', 'length'])
  const selectionOverride = {
    start: mask.length,
    end: mask.length,
  }

  // cursor positions let us know if the value has changed
  const newCursorPosition = getCursorPosition(newState)
  const oldCursorPosition = getCursorPosition(oldState)
  const newLength = getCursorSelectionLength(newState)
  const oldLength = getCursorSelectionLength(oldState)

  // if text is selected, block input
  if ((newLength || oldLength) && !isNull(userInput)) {
    return {
      value: oldValue,
      selection: selectionOverride,
    }
  }

  // cursor will change when user deletes input or blurs
  const cursorChanged = notEquals(newCursorPosition, oldCursorPosition)

  // handle user input
  if (not(cursorChanged) && !isNull(userInput)) {
    const updatedValue = appendToTimerValue(userInput, oldValue, mask)

    return {
      value: updatedValue,
      selection: selectionOverride,
    }
  }

  // handle user deletion
  // if new cursor position is null, we blurred so don't delete
  if (cursorChanged && !isNull(newCursorPosition) && isNull(userInput)) {
    const updatedValue = popTimerValue(newValue, mask)

    return {
      value: updatedValue,
      selection: selectionOverride,
    }
  }

  // default case on render
  return {
    value: newValue,
    selection: selectionOverride,
  }
}
